import PropTypes from 'prop-types'
import React, {
  createContext,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { Button, isDisabled, reportProblems } from './Button'
import { HoverGlidePill, useHoverGlide } from './useHoverGlide'

const MenuContext = createContext({ id: null, depth: 0, open: true })

// In listbox mode the menu is controlled: the trigger shows the requested value
// while onSelect's request is pending, then the parent's selected prop again.
export function Menu({
  id: suppliedId,
  label,
  valueLabel,
  placeholder,
  iconOnly = false,
  icon = 'fa-regular fa-ellipsis-vertical',
  swatch,
  align = 'start',
  placement = 'below',
  children,
  mode = 'menu',
  className,
  selected,
  onSelect,
  submenu = false,
  onSuccess,
  onFailure,
  onCancel,
  onOpenChange,
  surfaceLabel,
  minWidth,
  anchorPoint,
  onAfterClose,
  ...feedback
}) {
  const generatedId = useId()
  const id = suppliedId ?? generatedId
  const parent = useContext(MenuContext)
  const parentOpen = useRef(parent.open)
  parentOpen.current = parent.open
  const [phase, setPhase] = useState('closed')
  const currentPhase = useRef(phase)
  currentPhase.current = phase
  // Reports in the same event as the phase change, so a parent never lays out
  // against a stale open state.
  const openChange = useRef(onOpenChange)
  openChange.current = onOpenChange
  const reported = useRef(false)
  const report = next => {
    if (reported.current === next) return
    reported.current = next
    openChange.current?.(next)
  }
  const [requested, setRequested] = useState(null)
  const open = phase !== 'closed'
  const trigger = useRef(null)
  // The trigger's request outlives the trigger, so a selection that unmounts
  // the menu still settles through it.
  const requests = useRef({})
  const surface = useRef(null)
  const hoverPill = useHoverGlide(surface, '.me-menu-item', mode !== 'panel')
  const returnTarget = useRef(null)
  const previousTarget = useRef(null)
  const afterClose = useRef(onAfterClose)
  afterClose.current = onAfterClose
  const pointAnchored = anchorPoint !== undefined
  const control = () => trigger.current || surface.current
  const search = useRef({ text: '', time: 0 })
  const intent = useRef(null)
  const closing = useRef(() => {})
  const [host] = useState(() => document.createElement('div'))
  useLayoutEffect(() => {
    window.MEButtons.menuLayer(
      anchorPoint?.returnFocus || trigger.current
    ).append(host)
    return () => host.remove()
  }, [])
  const selection = requested ?? selected
  const disabled = isDisabled(feedback)
  useEffect(() => {
    if (!parent.open) clearTimeout(intent.current)
    // A parent closing restores focus itself.
    if (phase === 'open' && (disabled || !parent.open))
      close(
        false,
        undefined,
        parent.open && surface.current.contains(document.activeElement)
      )
  }, [disabled, parent.open])
  useEffect(
    () => () => {
      clearTimeout(intent.current)
      closing.current()
      // a menu removed while open reports that it closed
      report(false)
      // The trigger's request runs on; only its view goes. A remount, as
      // under StrictMode, starts a fresh one.
      requests.current.action?.dispose()
      requests.current.action = undefined
      requests.current.state = undefined
    },
    []
  )
  const show = () => {
    if (
      !parentOpen.current ||
      disabled ||
      window.MEButtons.isBlocked(control())
    )
      return
    if (pointAnchored) {
      if (!window.MEButtons.withinMenu(document.activeElement, surface.current))
        previousTarget.current = document.activeElement
      returnTarget.current = anchorPoint?.returnFocus || previousTarget.current
      window.MEButtons.menuLayer(returnTarget.current).append(host)
    }
    clearTimeout(intent.current)
    closing.current()
    surface.current
      ?.querySelector('.me-menu-selection')
      ?.classList.remove('me-menu-selection')
    if (surface.current) surface.current.inert = false
    setPhase('open')
    report(true)
  }
  const close = (advance = false, selected, restoreFocus = true) => {
    clearTimeout(intent.current)
    if (currentPhase.current === 'closed') return
    // A chosen action may have focused an editor or dialog of its own.
    const focused = document.activeElement
    if (selected && focused && focused !== document.body)
      restoreFocus =
        restoreFocus && window.MEButtons.withinMenu(focused, surface.current)
    currentPhase.current = 'closing'
    setPhase('closing')
    report(false)
    closing.current()
    closing.current = window.MEButtons.afterMenuExit(
      surface.current,
      () => {
        setPhase('closed')
        afterClose.current?.()
      },
      selected,
      control()
    )
    const button = pointAnchored
      ? returnTarget.current?.isConnected
        ? returnTarget.current
        : previousTarget.current
      : trigger.current
    if (!button || !restoreFocus) return
    if (advance) {
      const nodes = window.MEButtons.tabStops(document).filter(
        node => !surface.current?.contains(node)
      )
      const next =
        pointAnchored && !nodes.includes(button)
          ? nodes.find(node => button.compareDocumentPosition(node) & 4)
          : nodes[nodes.indexOf(button) + 1]
      if (pointAnchored) window.MEButtons.focusMenuTarget(next || button)
      else if (next) next.focus()
      else button.focus()
    } else if (pointAnchored) window.MEButtons.focusMenuTarget(button)
    else button.focus()
  }
  useLayoutEffect(() => {
    if (!pointAnchored) return
    if (anchorPoint) show()
    else close()
  }, [anchorPoint])
  useLayoutEffect(() => {
    if (
      phase !== 'open' ||
      currentPhase.current !== 'open' ||
      (pointAnchored && !anchorPoint)
    )
      return
    const button = trigger.current
    const menu = surface.current
    // A trigger scrolled out of view closes its menu without scrolling back.
    const position = () => {
      if (
        window.MEButtons.positionMenu(
          button || menu,
          menu,
          submenu,
          anchorPoint ? { ...anchorPoint, align, placement } : undefined
        )
      )
        return
      const focused = menu.contains(document.activeElement)
      close(false, undefined, false)
      if (focused) {
        if (pointAnchored)
          window.MEButtons.focusMenuTarget(returnTarget.current)
        else button.focus({ preventScroll: true })
      }
    }
    const scroll = event => {
      if (!window.MEButtons.withinMenu(event.target, menu)) close()
    }
    position()
    const initial =
      mode === 'listbox' ? menu.querySelector('[aria-selected="true"]') : null
    ;(
      initial ||
      (mode === 'panel'
        ? window.MEButtons.tabStops(menu)[0]
        : menu.querySelector(
            '[role="menuitem"]:not(:disabled), [role="option"]:not(:disabled)'
          )) ||
      menu
    ).focus()
    window.addEventListener('resize', position)
    window.addEventListener('scroll', pointAnchored ? scroll : position, true)
    return () => {
      window.removeEventListener('resize', position)
      window.removeEventListener(
        'scroll',
        pointAnchored ? scroll : position,
        true
      )
    }
  }, [phase, mode, submenu, anchorPoint])
  useEffect(() => {
    if (phase !== 'open') return
    const outside = event => {
      if (
        !window.MEButtons.withinMenu(event.target, surface.current) &&
        !trigger.current?.contains(event.target)
      )
        close()
    }
    document.addEventListener('pointerdown', outside)
    const unwatchIframe = window.MEButtons.watchIframeFocus(target => {
      if (!window.MEButtons.withinMenu(target, surface.current) &&
          !trigger.current?.contains(target)) close(false, undefined, false)
    })
    return () => {
      document.removeEventListener('pointerdown', outside)
      unwatchIframe()
    }
  }, [phase])
  // The host's listener runs after React's own there, so item handlers come
  // first and Escape stops before an enclosing dialog's handler.
  useEffect(() => {
    if (phase !== 'open') return
    const keydown = event => {
      if (event.defaultPrevented || !surface.current.contains(event.target))
        return
      if (event.key === 'Escape' || (submenu && event.key === 'ArrowLeft')) {
        event.stopPropagation()
        event.preventDefault()
        close()
        return
      }
      // An inline confirm opened from an action menu keeps its own buttons:
      // Tab moves between them and only Tab past either end leaves the menu,
      // so the menu's arrow keys and typeahead do not pull focus away.
      const confirm =
        mode !== 'panel' &&
        event.target.closest(
          '.me-inline-confirm.open .me-inline-confirm-controls'
        )
      if (confirm) {
        if (event.key !== 'Tab') return
        const controls = Array.from(confirm.querySelectorAll('button'))
        const at = controls.indexOf(event.target)
        if (event.shiftKey ? at > 0 : at >= 0 && at < controls.length - 1)
          return
      }
      const items =
        mode === 'panel'
          ? window.MEButtons.tabStops(surface.current)
          : Array.from(
              surface.current.querySelectorAll(
                '[role="menuitem"], [role="option"]'
              )
            )
      const index = items.indexOf(document.activeElement)
      if (event.key === 'Tab') {
        if (
          mode !== 'panel' ||
          index < 0 ||
          (!event.shiftKey && index === items.length - 1) ||
          (event.shiftKey && index === 0)
        ) {
          event.preventDefault()
          event.stopPropagation()
          ;(parent.exit || close)(!event.shiftKey)
        }
        return
      }
      if (mode === 'panel' || !items.length) return
      const next = window.MEButtons.menuIndex(
        event,
        items,
        index,
        search.current
      )
      if (next >= 0) {
        event.preventDefault()
        event.stopPropagation()
        items[next].focus()
      }
    }
    host.addEventListener('keydown', keydown)
    return () => host.removeEventListener('keydown', keydown)
  }, [phase, mode, submenu, parent.exit])
  if (parent.depth >= 3) {
    console.error('MEButtons: menus nest to at most three levels.')
    return null
  }
  const settle = callback => value => {
    setRequested(null)
    callback?.(value)
  }
  const choose = (child, value) => event => {
    if (window.MEButtons.isBlocked(control()) || isDisabled(child.props)) {
      event.preventDefault()
      return
    }
    if (mode === 'listbox' && onSelect) {
      const problems = window.MEButtons.validateAction(
        control().dataset,
        iconOnly
      )
      reportProblems(problems, control())
      if (problems.length) {
        event.preventDefault()
        return
      }
    }
    // Read first: either callback may unmount the menu.
    const owner = control()
    const { action } = requests.current
    if (child.props.onClick) child.props.onClick(event)
    if (event.defaultPrevented) return
    if (owner.isConnected) close(false, event.currentTarget)
    let result
    try {
      result = onSelect?.(value)
    } catch (error) {
      result = Promise.reject(error)
    }
    if (typeof result?.then !== 'function') return
    if (mode === 'listbox') setRequested(value)
    if (owner.isConnected) window.MEButtons.run(owner, result)
    else action?.run(result, owner.dataset)
  }
  const current = React.Children.toArray(children).find(
    child => child.props?.['data-value'] === selection
  )
  const role = mode === 'panel' ? 'dialog' : mode
  return (
    <>
      {!pointAnchored && (
        <Button
          {...feedback}
          ref={trigger}
          requestOwner={requests.current}
          className={
            submenu
              ? 'me-menu-item'
              : className ||
                (iconOnly
                  ? 'me-btn-icon'
                  : mode === 'listbox'
                  ? 'me-btn me-btn-select'
                  : 'me-btn me-btn-secondary me-btn-menu-trigger')
          }
          aria-label={label}
          data-tooltip={iconOnly ? label : feedback['data-tooltip']}
          aria-describedby={
            mode === 'listbox' || valueLabel !== undefined
              ? `${id}-value`
              : undefined
          }
          aria-haspopup={role}
          aria-expanded={phase === 'open'}
          aria-controls={id}
          data-menu-trigger={id}
          data-align={align}
          data-placement={placement}
          data-menu-min-width={minWidth}
          data-submenu={submenu ? '' : undefined}
          role={submenu ? 'menuitem' : undefined}
          tabIndex={submenu ? -1 : undefined}
          icon={iconOnly ? icon : undefined}
          iconAfter={
            iconOnly
              ? undefined
              : `fa-regular fa-chevron-${
                  submenu ? 'right' : 'down'
                } me-btn-caret`
          }
          onSuccess={settle(onSuccess)}
          onFailure={settle(onFailure)}
          onCancel={settle(onCancel)}
          onMouseEnter={() => {
            if (submenu) {
              clearTimeout(intent.current)
              intent.current = setTimeout(show, 120)
            }
          }}
          onMouseLeave={() => {
            if (submenu) {
              clearTimeout(intent.current)
              intent.current = setTimeout(() => close(), 300)
            }
          }}
          onClick={() => (phase === 'open' ? close() : show())}
          onKeyDown={event => {
            if (
              event.key === 'ArrowDown' ||
              (submenu && event.key === 'ArrowRight')
            ) {
              event.preventDefault()
              event.stopPropagation()
              show()
            }
          }}
        >
          {iconOnly ? undefined : mode === 'listbox' ||
            valueLabel !== undefined ? (
            <>
              {swatch && (
                <span
                  className="me-btn-select-swatch"
                  style={{ background: swatch }}
                  aria-hidden="true"
                />
              )}
              {mode === 'listbox' && (
                <span className="me-btn-select-label">{label}</span>
              )}
              <span className="me-btn-select-value" id={`${id}-value`}>
                {valueLabel ?? (current?.props.children || placeholder)}
              </span>
            </>
          ) : (
            label
          )}
        </Button>
      )}
      {createPortal(
        <div
          id={id}
          ref={surface}
          hidden={!open}
          role={role}
          aria-label={surfaceLabel ?? label}
          tabIndex={-1}
          className={`me-menu-surface${phase === 'open' ? ' open' : ''}`}
          data-panel={mode === 'panel' ? '' : undefined}
          data-me-hover-react=""
          data-menu-min-width={pointAnchored ? minWidth : undefined}
          data-me-menu-parent={parent.id || undefined}
          onMouseEnter={() => clearTimeout(intent.current)}
          onMouseLeave={() => {
            if (submenu) intent.current = setTimeout(() => close(), 300)
          }}
        >
          {mode !== 'panel' && <HoverGlidePill ref={hoverPill} />}
          <MenuContext.Provider
            value={{
              id,
              depth: parent.depth + 1,
              open: phase === 'open' && parent.open,
              exit: parent.exit || close,
            }}
          >
            {mode === 'panel'
              ? children
              : React.Children.map(children, child => {
                  if (
                    !React.isValidElement(child) ||
                    child.type === Menu ||
                    child.props.role === 'presentation' ||
                    child.props.role === 'separator'
                  )
                    return child
                  const value = child.props['data-value']
                  return React.cloneElement(child, {
                    role: mode === 'listbox' ? 'option' : 'menuitem',
                    tabIndex: -1,
                    'aria-selected':
                      mode === 'listbox' ? selection === value : undefined,
                    ...(child.type === Button && !child.props.className
                      ? { className: 'me-menu-item' }
                      : {}),
                    ...(mode === 'listbox' && child.type === Button
                      ? { iconAfter: 'fa-regular fa-check me-menu-check' }
                      : {}),
                    onClick: choose(child, value),
                  })
                })}
          </MenuContext.Provider>
        </div>,
        host
      )}
    </>
  )
}

Menu.propTypes = {
  id: PropTypes.string,
  label: PropTypes.string.isRequired,
  // The trigger's current value, for a trigger that reports one outside
  // listbox mode.
  valueLabel: PropTypes.node,
  // A listbox trigger's text with nothing selected ("Choose a view…").
  placeholder: PropTypes.string,
  iconOnly: PropTypes.bool,
  icon: PropTypes.string,
  // A color token reference, such as var(--brand), shown before the value.
  swatch: PropTypes.string,
  align: PropTypes.oneOf(['start', 'end']),
  placement: PropTypes.oneOf(['below', 'above']),
  children: PropTypes.node,
  mode: PropTypes.oneOf(['menu', 'listbox', 'panel']),
  className: PropTypes.string,
  selected: PropTypes.string,
  // Receives the chosen item's data-value. In listbox mode it returns the
  // request's promise, and the trigger's feedback attributes are required.
  onSelect: PropTypes.func,
  submenu: PropTypes.bool,
  onSuccess: PropTypes.func,
  onFailure: PropTypes.func,
  onCancel: PropTypes.func,
  // Called with true when the menu opens and false when it starts closing.
  onOpenChange: PropTypes.func,
  // The surface's accessible name, when it differs from the trigger's label.
  surfaceLabel: PropTypes.string,
  // The surface's minimum width in CSS pixels, in place of the default 220;
  // it never exceeds the viewport.
  minWidth: PropTypes.number,
  // Replaces the trigger when supplied. Changing the point opens or repositions
  // the menu; null closes it. Coordinates are viewport CSS pixels.
  anchorPoint: PropTypes.shape({
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired,
    returnFocus: PropTypes.object,
  }),
  // After exit motion, safe for a context-menu owner to unmount the menu.
  onAfterClose: PropTypes.func,
}
